// Electrical Section - Progressive 1D → 2D → 3D Navigation
import { useEffect, useState } from 'preact/hooks'
import { ELECTRICAL_SECTION, renderDefinition } from '../../../data/definitions.js'
import { basis, domain } from '../../../state/globalSignals.js'
import { formatValue } from '../../../utils/formatUtils.js'
import DimensionView from '../plots/DimensionView.jsx'

// SVG Icons
const EnergyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
  </svg>
)

const PowerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
)

const VoltageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
)

const ACImpedanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
  </svg>
)

const DCResistanceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="8" width="16" height="8" rx="1" ry="1"></rect>
    <line x1="2" y1="12" x2="4" y2="12"></line>
    <line x1="20" y1="12" x2="22" y2="12"></line>
  </svg>
)

// Color scheme for 4 experimental conditions
const CONDITION_COLORS = {
  c10: '#ea5b0c',      // Orange - C/10
  dc_25: '#0077b6',    // Blue - Standard
  cont: '#d62828',     // Red - Continuous
  peak: '#48cae4'      // Cyan - Peak
}

// Helper: Get multiple values for category based on current basis/domain
function getMultipleValues(cellData, categoryId, currentBasis, currentDomain) {
  if (!cellData?.electrical) return null

  const elec = cellData.electrical
  const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
  const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null

  switch(categoryId) {
    case 'energy': {
      // Energy/Capacity: Only C/10 value
      if (currentDomain === 'energy-power') {
        // Energy [Wh] - C/10 only
        let w_c10
        let unit = 'Wh'

        if (currentBasis === 'grav') {
          const e_c10_abs = elec.E_c10_Wh || 0
          w_c10 = elec.E_grav_Wh_per_kg || (m_kg ? e_c10_abs / m_kg : 0)
          unit = 'Wh/kg'
        } else if (currentBasis === 'vol') {
          const e_c10_abs = elec.E_c10_Wh || 0
          w_c10 = elec.E_vol_Wh_per_L || (vol_L ? e_c10_abs / vol_L : 0)
          unit = 'Wh/L'
        } else {
          w_c10 = elec.E_c10_Wh || 0
        }

        return {
          type: '1-value',
          values: [
            { value: w_c10, unit, label: 'C/10', color: CONDITION_COLORS.c10 }
          ]
        }
      } else {
        // Capacity [Ah] - C/10 only
        let c_c10
        let unit = 'Ah'

        if (currentBasis === 'grav') {
          const c_c10_abs = elec.C_c10_Ah || 0
          c_c10 = elec.C_grav_Ah_per_kg || (m_kg ? c_c10_abs / m_kg : 0)
          unit = 'Ah/kg'
        } else if (currentBasis === 'vol') {
          const c_c10_abs = elec.C_c10_Ah || 0
          c_c10 = elec.C_vol_Ah_per_L || (vol_L ? c_c10_abs / vol_L : 0)
          unit = 'Ah/L'
        } else {
          c_c10 = elec.C_c10_Ah || 0
        }

        return {
          type: '1-value',
          values: [
            { value: c_c10, unit, label: 'C/10', color: CONDITION_COLORS.c10 }
          ]
        }
      }
    }

    case 'power': {
      // Power/Current: Only Peak value
      if (currentDomain === 'energy-power') {
        // Power [W] - Peak only
        let p_peak
        let unit = 'W'

        if (currentBasis === 'grav') {
          const p_peak_abs = elec.P_dc_peak || 0
          p_peak = m_kg ? p_peak_abs / m_kg : 0
          unit = 'W/kg'
        } else if (currentBasis === 'vol') {
          const p_peak_abs = elec.P_dc_peak || 0
          p_peak = vol_L ? p_peak_abs / vol_L : 0
          unit = 'W/L'
        } else {
          p_peak = elec.P_dc_peak || 0
        }

        return {
          type: '1-value',
          values: [
            { value: p_peak, unit, label: 'Peak', color: CONDITION_COLORS.peak }
          ]
        }
      } else {
        // Current [A] - Peak only
        let i_peak
        let unit = 'A'

        if (currentBasis === 'grav') {
          const i_peak_abs = elec.I_dc_peak || 0
          i_peak = m_kg ? i_peak_abs / m_kg : 0
          unit = 'A/kg'
        } else if (currentBasis === 'vol') {
          const i_peak_abs = elec.I_dc_peak || 0
          i_peak = vol_L ? i_peak_abs / vol_L : 0
          unit = 'A/L'
        } else {
          i_peak = elec.I_dc_peak || 0
        }

        return {
          type: '1-value',
          values: [
            { value: i_peak, unit, label: 'Peak', color: CONDITION_COLORS.peak }
          ]
        }
      }
    }

    case 'voltage': {
      // Voltage: Only Mean value
      const v_mean = elec.V_mean || 0

      return {
        type: '1-value',
        values: [
          { value: v_mean, unit: 'V', label: 'Mean', color: '#0077b6' }
        ]
      }
    }

    case 'ac-impedance': {
      // Impedance: AC and Polarisation
      const r_ac = elec.R_ac_mOhm || 0
      const r_pol = elec.R_pol_25 || 0

      return {
        type: '2-values',
        values: [
          { value: r_ac, unit: 'mΩ', label: 'AC', color: '#0077b6' },
          { value: r_pol, unit: 'mΩ', label: 'Polarisation', color: '#ea5b0c' }
        ]
      }
    }

    case 'dc-resistance': {
      // DCIR: single value with color chip
      const rValue = elec.R_dc_mOhm || 0

      return {
        type: '1-value',
        values: [
          { value: rValue, unit: 'mΩ', label: 'DCIR', color: '#ea5b0c' }
        ]
      }
    }

    default:
      return null
  }
}

// Dynamic category labels based on domain signal
function getCategoryLabel(categoryId, currentDomain) {
  if (categoryId === 'energy') {
    return currentDomain === 'energy-power' ? 'Energy' : 'Capacity'
  }
  if (categoryId === 'power') {
    return currentDomain === 'energy-power' ? 'Power' : 'Current'
  }
  return null
}

const CATEGORIES = [
  {
    id: 'energy',
    label: 'Energy',  // Default, will be overridden dynamically
    icon: <EnergyIcon />
  },
  {
    id: 'power',
    label: 'Power',   // Default, will be overridden dynamically
    icon: <PowerIcon />
  },
  {
    id: 'voltage',
    label: 'Voltage',
    icon: <VoltageIcon />
  },
  {
    id: 'ac-impedance',
    label: 'Impedance',
    icon: <ACImpedanceIcon />
  },
  {
    id: 'dc-resistance',
    label: 'Resistance',
    icon: <DCResistanceIcon />
  }
]

export default function ElectricalSection({ cellData }) {
  const [activeCategory, setActiveCategory] = useState('energy')
  // Load dimension from localStorage, fallback to '2d'
  const [dimension, setDimension] = useState(() => {
    return localStorage.getItem('batemo:dimension') || '2d'
  })
  // Flipcard state for each category
  const [flippedCards, setFlippedCards] = useState({})

  // Save dimension to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('batemo:dimension', dimension)
  }, [dimension])

  // When switching categories, always default to 2D (Nyquist for AC Impedance)
  useEffect(() => {
    setDimension('2d')
  }, [activeCategory])

  const activeCat = CATEGORIES.find(c => c.id === activeCategory)

  // Toggle flip for current category
  const toggleFlip = () => {
    setFlippedCards(prev => ({
      ...prev,
      [activeCategory]: !prev[activeCategory]
    }))
  }

  // Get the definition for current category
  const getCategoryDefinition = () => {
    const defMap = {
      'energy': ELECTRICAL_SECTION.energy,
      'power': ELECTRICAL_SECTION.power,
      'voltage': ELECTRICAL_SECTION.voltage,
      'ac-impedance': ELECTRICAL_SECTION.acImpedance,
      'dc-resistance': ELECTRICAL_SECTION.dcResistance
    }
    const def = defMap[activeCategory] || { title: '', text: '' }
    // Use dynamic title if available, otherwise fallback to static title
    const dynamicTitle = def.getTitle ? def.getTitle(domain.value) : def.title
    return { ...def, title: dynamicTitle }
  }

  const currentDef = getCategoryDefinition()

  return (
    <div className="electrical-section-new">
      {/* Side Navigation Panel */}
      <aside className="electrical-nav" data-tour="electrical-nav">
        <div className="nav-header">
          <h3>Electrical Analysis</h3>
          <div className="nav-subtitle">Progressive 1D→2D→3D</div>
        </div>

        <div className="category-list">
          {CATEGORIES.map(cat => {
            const values = getMultipleValues(cellData, cat.id, basis.value, domain.value)
            // Dynamic label for energy/power based on domain
            const displayLabel = getCategoryLabel(cat.id, domain.value) || cat.label

            return (
              <button
                key={cat.id}
                className={`category-card ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat.id)
                }}
              >
                <div className="category-icon">{cat.icon}</div>
                <div className="category-info">
                  <div className="category-label">{displayLabel}</div>
                  <div className="category-desc">
                    {values ? (
                      <div className="category-values-chips">
                        {values.values.map((v, idx) => (
                            <div
                              key={idx}
                              className="value-chip"
                              style={`border-color: ${v.color}`}
                            >
                              <span className="value-chip-label" style={`color: ${v.color}`}>
                                {v.label}
                              </span>
                              <span className="value-chip-value" style={`color: ${v.color}`}>
                                {formatValue(v.value, v.unit)}
                                <span className="value-chip-unit" style={`color: ${v.color}`}>
                                  {v.unit}
                                </span>
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <span className="category-value-na">N/A</span>
                    )}
                  </div>
                </div>
                {activeCategory === cat.id && (
                  <div className="category-indicator"></div>
                )}
              </button>
            )
          })}
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="electrical-main" data-tour="electrical-main">
        {/* Dimension Stepper - Special for AC Impedance */}
        {activeCategory === 'ac-impedance' ? (
          <div className="dimension-stepper" data-tour="electrical-dimension-stepper">
            <button
              className={`dimension-btn ${dimension === '1d' ? 'active' : ''}`}
              onClick={() => setDimension('1d')}
            >
              <span className="dim-icon">●</span>
              <div className="dim-info">
                <span className="dim-label">Value</span>
                <span className="dim-sublabel">Single Metric</span>
              </div>
            </button>

            <div className="stepper-line"></div>

            <button
              className={`dimension-btn ${dimension === '2d' ? 'active' : ''}`}
              onClick={() => setDimension('2d')}
            >
              <span className="dim-icon">◗</span>
              <div className="dim-info">
                <span className="dim-label">Nyquist</span>
                <span className="dim-sublabel">Complex Plane</span>
              </div>
            </button>

            <div className="stepper-line"></div>

            <button
              className={`dimension-btn ${dimension === '3d' ? 'active' : ''}`}
              onClick={() => setDimension('3d')}
            >
              <span className="dim-icon">◭</span>
              <div className="dim-info">
                <span className="dim-label">Bode Plot</span>
                <span className="dim-sublabel">Magnitude & Phase</span>
              </div>
            </button>
          </div>
        ) : (
          <div className="dimension-stepper" data-tour="electrical-dimension-stepper">
            <button
              className={`dimension-btn ${dimension === '1d' ? 'active' : ''}`}
              onClick={() => setDimension('1d')}
            >
              <span className="dim-icon">●</span>
              <div className="dim-info">
                <span className="dim-label">Value</span>
                <span className="dim-sublabel">Single Metric</span>
              </div>
            </button>

            <div className="stepper-line"></div>

            <button
              className={`dimension-btn ${dimension === '2d' ? 'active' : ''}`}
              onClick={() => setDimension('2d')}
            >
              <span className="dim-icon">◧</span>
              <div className="dim-info">
                <span className="dim-label">2D Plot</span>
                <span className="dim-sublabel">Line Charts</span>
              </div>
            </button>

            {/* Conditional 3D - hide for Voltage */}
            {activeCategory !== 'voltage' && (
              <>
                <div className="stepper-line"></div>

                <button
                  className={`dimension-btn ${dimension === '3d' ? 'active' : ''}`}
                  onClick={() => setDimension('3d')}
                >
                  <span className="dim-icon">◰</span>
                  <div className="dim-info">
                    <span className="dim-label">Surface Plot</span>
                    <span className="dim-sublabel">3D Landscape</span>
                  </div>
                </button>
              </>
            )}
          </div>
        )}

        {/* Content Area - Flipcard */}
        <div className={`electrical-flipcard flipcard ${flippedCards[activeCategory] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            {/* Front: Chart/Plot */}
            <div className="flipcard-front electrical-flipcard-front">
              <button className="flip-btn" onClick={toggleFlip} title="Show info">?</button>
              <h3 className="column-title">{currentDef.title}</h3>

              <DimensionView
                category={activeCategory}
                dimension={dimension}
                cellData={cellData}
                domainValue={domain.value}
                onToggleDimension={(newDim) => setDimension(newDim)}
              />
            </div>

            {/* Back: Definition */}
            <div className="flipcard-back electrical-flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={toggleFlip} title="Close">×</button>
              <h3 className="column-title">{currentDef.title}</h3>
              <div className="flip-content">
                <p dangerouslySetInnerHTML={{ __html: renderDefinition(currentDef.text, cellData) }} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
